/**
 * 用户权限管理工具
 * 统一管理用户的各种权限检查
 */

/**
 * 检查用户是否具有指定权限
 * @param {string} permissionCode - 权限代码
 * @param {Object} versionInfo - 版本信息对象（可选，如果不提供则从localStorage获取）
 * @returns {boolean} 是否具有该权限
 */
export function hasPermission(permissionCode, versionInfo = null) {

    // 如果提供了versionInfo，使用它；否则从localStorage获取
    let versionFunctions, versionExpireTimeStr;

    if (versionInfo) {
        versionFunctions = versionInfo.versionFunctions || [];
        versionExpireTimeStr = versionInfo.versionExpireTime || '';
    } else {
        versionFunctions = JSON.parse(localStorage.getItem('version_functions') || '[]');
        versionExpireTimeStr = localStorage.getItem('version_expire_time') || '';
    }

    const currentDate = new Date();
    const versionExpireDate = new Date(versionExpireTimeStr);

    // 检查权限是否存在且未过期
    const hasFunction = versionFunctions.indexOf(permissionCode) >= 0;
    const notExpired = currentDate.getTime() <= versionExpireDate.getTime();

    return hasFunction && notExpired;
}

/**
 * 检查用户是否需要看广告
 * @param {Object} versionInfo - 版本信息对象（可选）
 * @returns {boolean} true-需要看广告，false-不需要看广告
 */
export function adStatus(versionInfo = null) {
    return !hasPermission('no_ad', versionInfo);
}

/**
 * 检查用户是否有复制权限
 * @param {Object} versionInfo - 版本信息对象（可选）
 * @returns {boolean} 是否具有复制权限
 */
export function hasCopyPermission(versionInfo = null) {
    return hasPermission('copy', versionInfo);
}

/**
 * 检查用户是否有AI分析权限
 * @param {Object} versionInfo - 版本信息对象（可选）
 * @returns {boolean} 是否具有AI分析权限（前端仅用于UI提示）
 */
export function hasAIAnalysisPermission(versionInfo = null) {
    // 注意：实际权限校验（包括免费试用次数）由后端控制
    // 前端不做 ai_analysis 硬拦截，避免挡住“免费3次”能力
    return true;
}

/**
 * 检查用户是否有同屏对比权限
 * @param {Object} versionInfo - 版本信息对象（可选）
 * @returns {boolean} 是否具有同屏对比权限
 */
export function hasSideComparePermission(versionInfo = null) {
    return hasPermission('side_compare', versionInfo);
}

/**
 * 显示权限不足的提示
 * @param {Array} missingPermissions - 缺少的权限列表
 * @param {Object} vm - Vue实例，用于显示弹窗
 */
export function showPermissionDeniedTip(missingPermissions, vm = null) {
    const permissionNames = {
        'no_ad': '免广告',
        'copy': '复制',
        'ai_analysis': 'AI分析',
        'side_compare': '同屏对比'
    };

    const missingNames = missingPermissions.map(code => permissionNames[code] || code).join('、');

    // 如果有Vue实例，使用Element UI的弹窗
    if (vm && vm.$alert) {
        vm.$alert(`您需要${missingNames}权限才能使用此功能，请升级版本后重试。`, '权限不足', {
            confirmButtonText: '我知道了',
            type: 'warning',
            callback: () => {
                // 可以在这里添加跳转到升级页面的逻辑
                // vm.$router.push('/upgrade');
            }
        });
    } else {
        // 降级处理：使用原生弹窗
        alert(`您需要${missingNames}权限才能使用此功能，请升级版本后重试。`);
    }
}

/**
 * 权限名称映射
 * @returns {Object} 权限代码到名称的映射对象
 */
export const PERMISSION_NAMES = {
    'no_ad': '免广告',
    'copy': '复制',
    'ai_analysis': 'AI分析',
    'side_compare': '同屏对比'
};

/**
 * VIP权限代码列表
 * @returns {Array} VIP权限代码数组
 */
export const VIP_PERMISSIONS = ['no_ad', 'copy', 'ai_analysis', 'side_compare'];
